import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { CmsBlock } from "@gaole/shared";
import { api } from "../lib/api";
import { CmsBlockRenderer } from "../components/CmsBlockRenderer";
import { PageLoader } from "../components/ui";

interface CmsPageResponse {
  page: {
    slug: string;
    title: string;
    excerpt: string | null;
    blocks: CmsBlock[];
    updatedAt: string;
  };
}

export function CmsPage() {
  const { slug = "" } = useParams<{ slug: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["cms-page", slug],
    queryFn: () => api<CmsPageResponse>(`/content/pages/${slug}`),
  });

  if (isLoading) return <PageLoader />;

  if (isError || !data) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="font-display text-sm text-white/70">This page is not available.</p>
        <Link to="/" className="mt-3 inline-block text-xs font-semibold text-brand-pink">
          Back to home
        </Link>
      </div>
    );
  }

  const page = data.page;

  return (
    <article className="px-4 py-6">
      <header className="mb-5 border-b border-white/10 pb-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40">Article</p>
        <h1 className="mt-1 font-display text-xl font-bold">{page.title}</h1>
        {page.excerpt ? <p className="mt-2 text-sm text-white/60">{page.excerpt}</p> : null}
        {page.updatedAt && (
          <p className="mt-1 text-[11px] text-white/40">
            Updated {new Date(page.updatedAt).toLocaleDateString()}
          </p>
        )}
      </header>
      <CmsBlockRenderer blocks={page.blocks} />
    </article>
  );
}
